import * as Sentry from '@sentry/react';
import Axios from 'axios';

const AxiosCommonHeaders = { 'X-Requested-With': 'XMLHttpRequest' };

// Retry only idempotent requests, once, with a short linear backoff.
const MAX_RETRIES = 1;
const RETRY_BASE_DELAY_MS = 800;

// A burst of concurrent failures collapses into a single consolidated notice; a sustained stream
// still re-notifies at most once per cooldown window (leading-edge throttle, never starves).
const NOTICE_COOLDOWN_MS = 5000;
const BACKGROUND_FAILURE_MESSAGE = 'Some information could not be loaded. You can keep working; we will keep trying in the background.';
const MUTATION_FAILURE_MESSAGE = 'Your last action could not be completed due to a network issue. Please try again.';

const lastNoticeAt = {
    background: 0,
    mutation: 0,
};

export const apiClient = Axios.create({
    headers: { common: AxiosCommonHeaders },
});

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const handleRedirectResponse = (response) => {
    // Where any contingency the request returned went. Shown before the redirect
    // below, which it outlives.
    window.showContingencyNoticeFromHeader?.(response.headers?.['x-contingency-notice']);

    const redirectUrl = response.data?.redirect_url;

    if (redirectUrl) {
        window.location.href = redirectUrl;
        return Promise.reject();
    }

    return response;
};

/**
 * A network-level error means the request never reached (or never heard back from)
 * the server: no HTTP response was received. A cancelled request is deliberate, so
 * it is never treated as a network failure (never retried, never counted).
 */
const isNetworkLevelError = (error) => {
    if (error.code === 'ERR_CANCELED') {
        return false;
    }

    if (error.code === 'ERR_NETWORK') {
        return true;
    }

    return !error.response && !!error.request;
};

/**
 * Only idempotent methods are safe to replay automatically — replaying a
 * POST/PUT/PATCH/DELETE risks a double submit.
 */
const isRetryableRequest = (config) => {
    const method = (config?.method || 'get').toLowerCase();

    return ['get', 'head', 'options'].includes(method);
};

// Shared rich context, applied to Sentry for both network and HTTP failures.
const buildErrorInfo = (error) => ({
    message: error.message,
    request: {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
    },
    response: {
        status: error.response?.status,
        message: error.response?.data?.message,
    },
});

const applyErrorContext = (scope, error, errorInfo) => {
    // Add breadcrumb to Sentry for the Axios request
    scope.addBreadcrumb({
        category: 'axios',
        message: `Request to ${errorInfo.request.url} failed during navigation`,
        level: 'info',
    });
    scope.setTransactionName(`${errorInfo.request.method} ${errorInfo.request.url}`);
    scope.setTag('http_method', errorInfo.request.method);
    scope.setTag('http_status', error.response?.status);

    // Add backend error context if available (works in both dev and production)
    if (error.response?.data) {
        const backendData = error.response.data;

        // Add any available backend error details
        if (backendData.message) {
            scope.setContext('backend_error', {
                message: backendData.message,
            });
        }

        // In development, Laravel may include exception details
        if (backendData.exception) {
            scope.setContext('backend_exception', {
                class: backendData.exception,
                file: backendData.file,
                line: backendData.line,
            });

            if (backendData.trace) {
                scope.setExtra('backend_trace', backendData.trace);
            }
        }

        // Add validation errors if present
        if (backendData.errors) {
            scope.setContext('validation_errors', backendData.errors);
        }
    }

    scope.setExtra('info', errorInfo);
};

// Genuine HTTP errors (4xx/5xx) keep the existing capture behaviour — the crash
// report dialog still fires downstream via Sentry's beforeSend.
const captureHttpError = (error) => {
    const errorInfo = buildErrorInfo(error);

    Sentry.withScope((scope) => {
        applyErrorContext(scope, error, errorInfo);
        Sentry.captureException(error);
    });
};

/**
 * A caller can opt specific response statuses out of the default "genuine HTTP error"
 * treatment by passing `expectedErrorStatuses: [422, ...]` in the request config (third
 * argument to the axios call). This is for business-rule rejections the caller already
 * surfaces via its own message (e.g. a toast) — a real, known outcome rather than a bug —
 * so it should not pop the blocking crash-report dialog. Unlisted statuses on the same
 * request (e.g. a genuine 500) are unaffected and keep the default dialog behaviour.
 */
const isExpectedResponseStatus = (error) => {
    const expected = error.config?.expectedErrorStatuses;

    return Array.isArray(expected) && !!error.response && expected.includes(error.response.status);
};

// A caller-flagged "expected" HTTP error (see `isExpectedResponseStatus`) — still sent to
// Sentry for tracing, but tagged so the crash report dialog is suppressed, since the
// caller already surfaces its own message for this outcome.
const captureExpectedHttpError = (error) => {
    const errorInfo = buildErrorInfo(error);

    Sentry.withScope((scope) => {
        scope.setTag('suppress_report_dialog', true);
        scope.setLevel('warning');
        applyErrorContext(scope, error, errorInfo);
        Sentry.captureException(error);
    });
};

// Network-level noise is still sent to Sentry (as a warning) so we can trace it,
// but tagged so the crash report dialog is suppressed downstream.
const captureNetworkError = (error) => {
    const errorInfo = buildErrorInfo(error);

    Sentry.withScope((scope) => {
        scope.setTag('suppress_report_dialog', true);
        scope.setLevel('warning');
        applyErrorContext(scope, error, errorInfo);
        Sentry.captureException(error);
    });
};

// Leading-edge cooldown so a burst of concurrent failures of the same kind produces exactly one
// toast, while a sustained stream still re-notifies at most once per cooldown window. Background
// reads and mutations are tracked separately so a burst of one kind never suppresses the other.
const notifyOnce = (kind, message, toastType) => {
    const now = Date.now();

    if (now - lastNoticeAt[kind] < NOTICE_COOLDOWN_MS) {
        return;
    }

    lastNoticeAt[kind] = now;

    // Degrade silently if the global toast helper is unavailable — Sentry still logged it.
    if (typeof window.showToast !== 'function') {
        return;
    }

    const translated = typeof window.$t === 'function' ? window.$t(message) : message;

    window.showToast(translated, toastType);
};

// A background read failed: it will be retried automatically, so the user can keep working.
const notifyBackgroundFailureOnce = () => notifyOnce('background', BACKGROUND_FAILURE_MESSAGE, 'warning');

// A mutation (POST/PUT/PATCH/DELETE) failed at the network level: it is not retried automatically,
// so the user needs to know their action did not go through and may need to retry it themselves.
const notifyMutationFailureOnce = () => notifyOnce('mutation', MUTATION_FAILURE_MESSAGE, 'error');

/**
 * The set-up gate refused the first procurement item or PR on a project
 * because a set-up wizard page is unfinished. Call sites check this before
 * showing their own generic error, so the user only sees the gate's message.
 */
export const isSetupGateError = (error) => error?.response?.status === 423 && error.response.data?.error_code === 'SETUP_INCOMPLETE';

// An Event Admin is sent to the wizard page; anyone else is told to ask their
// Event Admin. The message is already localised by the server. Not reported to
// Sentry: it is an expected business outcome, not a fault.
const handleSetupGateError = (error) => {
    const { message, redirect_url: redirectUrl } = error.response.data;

    error.handled = true;

    if (redirectUrl) {
        window.location.href = redirectUrl;
    } else if (typeof window.showToast === 'function') {
        window.showToast(message, 'error');
    }

    return Promise.reject(error);
};

const handleAxiosError = async (error) => {
    // Deliberately-aborted requests (e.g. routine refetch cancellations) are not failures:
    // never retry, never notify, never report.
    if (error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
    }

    const config = error.config;

    if (isNetworkLevelError(error)) {
        const retryable = config && isRetryableRequest(config);

        // Transient network failure on an idempotent (background read) request: retry quietly once.
        if (retryable && (config.__retryCount || 0) < MAX_RETRIES) {
            config.__retryCount = (config.__retryCount || 0) + 1;
            await delay(RETRY_BASE_DELAY_MS * config.__retryCount);

            return apiClient(config);
        }

        // Log to Sentry (dialog stays suppressed either way). Background reads get the "keep
        // trying" notice, since they are retried automatically; mutations are not retried, so the
        // user is told their action did not go through instead.
        captureNetworkError(error);
        if (retryable) {
            notifyBackgroundFailureOnce();
        } else {
            notifyMutationFailureOnce();
        }

        return Promise.reject(error);
    }

    if (isSetupGateError(error)) {
        return handleSetupGateError(error);
    }

    // Genuine HTTP error (a response was received) — keep the existing capture behaviour,
    // unless the caller explicitly flagged this status as an expected, already-handled
    // outcome (see `isExpectedResponseStatus`).
    if (isExpectedResponseStatus(error)) {
        captureExpectedHttpError(error);
    } else {
        captureHttpError(error);
    }

    return Promise.reject(error);
};

apiClient.interceptors.response.use(
    (response) => handleRedirectResponse(response),
    (error) => handleAxiosError(error),
);

/**
 * An Axios instance when used the data won't be dynamically translated,
 * useful for forms
 */
apiClient.untranslated = function () {
    return Axios.create({
        headers: { common: { ...AxiosCommonHeaders, 'X-Untranslated': true } },
    });
};
